import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Lock, Bolt, ShieldCheck } from 'lucide-react';
import { supabase } from '@/lib/supabase';

export default function PasswordReset() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setHasSession(Boolean(data.session));
      setReady(true);
    });
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (mounted) setHasSession(Boolean(session));
    });
    return () => { mounted = false; listener.subscription.unsubscribe(); };
  }, []);

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (password.length < 10) { setError('Use at least 10 characters.'); return; }
    setBusy(true); setError(null); setNotice(null);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      setNotice('Your password has been updated. Redirecting to your workspace…');
      window.setTimeout(() => navigate('/workspace', { replace: true }), 700);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not update your password.');
    } finally { setBusy(false); }
  };

  return <div className="auth-page">
    <header className="auth-top">
      <Link to="/" className="brand" aria-label="Enerlectra home"><span className="brand-mark"><Bolt size={18} fill="currentColor"/></span><span>ENERLECTRA</span><span className="brand-divider"/><span className="brand-sub">THE ENERGY INTERNET</span></Link>
      <Link to="/signin" className="text-link">Back to sign in</Link>
    </header>
    <main className="auth-grid">
      <section className="auth-form-side">
        <div className="auth-form-box">
          <span className="eyebrow"><span className="live-dot"/> ACCOUNT SECURITY</span>
          <h1>Reset your password.</h1>
          {!ready ? <p>Verifying your secure recovery link…</p> : !hasSession ? <><p>This password-reset link is invalid or has expired. Request a new link from the sign-in page.</p><Link className="button button-dark full-button" to="/signin">Return to sign in</Link></> : <>
            <p>Choose a new password for your Enerlectra account.</p>
            <form onSubmit={submit} className="form-stack">
              <label className="field-wrap"><span className="field-label">New password</span><span className="auth-input-wrap"><Lock size={16}/><input className="field auth-input" type="password" autoComplete="new-password" minLength={10} required value={password} onChange={event => setPassword(event.target.value)} placeholder="At least 10 characters" disabled={busy}/></span></label>
              {error && <p className="auth-error" role="alert">{error}</p>}
              {notice && <p className="auth-notice" role="status">{notice}</p>}
              <button className="button button-dark full-button auth-submit" disabled={busy}>{busy ? 'Updating…' : 'Update password'}</button>
            </form>
          </>}
        </div>
      </section>
      <aside className="auth-aside"><div className="auth-aside-content"><span className="aside-icon"><Bolt size={25} fill="currentColor"/></span><h2>Secure access. Verified authority.</h2><p>Password recovery restores account access; organization permissions remain controlled by active membership.</p></div><div className="auth-aside-foot"><ShieldCheck size={15}/> PRIVATE ORGANIZATION WORKSPACES</div></aside>
    </main>
  </div>;
}
